import { supabase } from '@/integrations/supabase/client';

declare global {
  interface Window {
    fbq: (...args: any[]) => void;
  }
}

// Generate unique event ID for deduplication
const generateEventId = () => crypto.randomUUID();

// Get Facebook cookies
const getFacebookCookies = () => {
  const cookies = document.cookie.split(';').reduce((acc, cookie) => {
    const [key, value] = cookie.trim().split('=');
    acc[key] = value;
    return acc;
  }, {} as Record<string, string>);

  return {
    fbp: cookies._fbp || null,
    fbc: cookies._fbc || null,
  };
};

export const useFacebookPixel = () => {
  // Dual tracking: Send to both Pixel (client-side) and CAPI (server-side)
  const trackEvent = async (
    eventName: string,
    eventParams?: Record<string, any>
  ) => {
    const eventId = generateEventId();

    // 1. Track via Facebook Pixel (client-side)
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', eventName, eventParams, { eventID: eventId });
    }

    // 2. Track via Conversion API (server-side)
    try {
      const { fbp, fbc } = getFacebookCookies();
      const { data: { user } } = await supabase.auth.getUser();

      // Extract additional user data from metadata if available
      const userMetadata = user?.user_metadata || {};
      
      // Priority order for email matching:
      // 1. Logged-in user email (highest priority)
      // 2. Pre-captured email from localStorage (for anonymous users)
      // 3. null (no email available)
      const capturedEmail = localStorage.getItem('captured_email_for_tracking');
      const capturedFirstName = localStorage.getItem('captured_first_name_for_tracking');
      
      await supabase.functions.invoke('facebook-conversion-api', {
        body: {
          event_name: eventName,
          event_id: eventId,
          event_source_url: window.location.href,
          custom_data: eventParams || {},
          email: user?.email || capturedEmail || null,
          external_id: user?.id || null, // User ID for better matching
          first_name: userMetadata.first_name || userMetadata.full_name?.split(' ')[0] || capturedFirstName || null,
          last_name: userMetadata.last_name || userMetadata.full_name?.split(' ').slice(1).join(' ') || null,
          phone: userMetadata.phone || null,
          fbp,
          fbc,
        },
      });
    } catch (error) {
      console.error('CAPI tracking error:', error);
      // Don't throw - pixel tracking already succeeded
    }
  };

  const trackCustomEvent = async (
    eventName: string,
    eventParams?: Record<string, any>
  ) => {
    const eventId = generateEventId();

    // 1. Track via Facebook Pixel (client-side)
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('trackCustom', eventName, eventParams, { eventID: eventId });
    }

    // 2. Track via Conversion API (server-side)
    try {
      const { fbp, fbc } = getFacebookCookies();
      const { data: { user } } = await supabase.auth.getUser();

      // Extract additional user data from metadata if available
      const userMetadata = user?.user_metadata || {};

      // Priority order for email matching:
      // 1. Logged-in user email (highest priority)
      // 2. Pre-captured email from localStorage (for anonymous users)
      // 3. null (no email available)
      const capturedEmail = localStorage.getItem('captured_email_for_tracking');
      const capturedFirstName = localStorage.getItem('captured_first_name_for_tracking');

      await supabase.functions.invoke('facebook-conversion-api', {
        body: {
          event_name: eventName,
          event_id: eventId,
          event_source_url: window.location.href,
          custom_data: eventParams || {},
          email: user?.email || capturedEmail || null,
          external_id: user?.id || null,
          first_name: userMetadata.first_name || userMetadata.full_name?.split(' ')[0] || capturedFirstName || null,
          last_name: userMetadata.last_name || userMetadata.full_name?.split(' ').slice(1).join(' ') || null,
          phone: userMetadata.phone || null,
          fbp,
          fbc,
        },
      });
    } catch (error) {
      console.error('CAPI tracking error:', error);
    }
  };

  return { trackEvent, trackCustomEvent };
};
